<template>
  <div class="media-comparison-viewer" @click.self="close">
    <!-- Desktop: side-by-side panels -->
    <template v-if="!isMobile">
      <div class="viewer-panels">
        <div v-if="originalPhotos.length > 0" class="panel">
          <div class="panel-header original-header">
            <span>Original Photos</span>
            <span class="photo-counter"
              >{{ originalIndex + 1 }}/{{ originalPhotos.length }}</span
            >
          </div>
          <div class="panel-body">
            <button
              v-if="originalPhotos.length > 1"
              class="nav-arrow left"
              aria-label="Previous original photo"
              @click="prevOriginal"
            >
              <span class="fa-solid fa-chevron-left" />
            </button>
            <div class="panel-image">
              <img
                v-if="
                  currentOriginalPhoto && !currentOriginalPhoto.attributes.video
                "
                :src="resolveUrl(currentOriginalPhoto)"
                alt="Original photo"
              />
              <video
                v-else-if="
                  currentOriginalPhoto && currentOriginalPhoto.attributes.video
                "
                controls
                preload="auto"
                :poster="cdnUrl(currentOriginalPhoto.attributes.video_thumb)"
              >
                <source
                  :src="cdnUrl(currentOriginalPhoto.attributes.video_url)"
                  type="video/mp4"
                />
              </video>
            </div>
            <button
              v-if="originalPhotos.length > 1"
              class="nav-arrow right"
              aria-label="Next original photo"
              @click="nextOriginal"
            >
              <span class="fa-solid fa-chevron-right" />
            </button>
            <div v-if="hasCaption(currentOriginalPhoto)" class="photo-caption">
              {{ currentOriginalPhoto.attributes.caption }}
            </div>
          </div>
          <div class="panel-thumbnails">
            <div
              v-for="(photo, idx) in originalPhotos"
              :key="photo.id"
              class="thumb"
              :class="{ active: idx === originalIndex }"
              @click="originalIndex = idx"
            >
              <img :src="resolveThumb(photo)" />
            </div>
          </div>
        </div>
        <div v-else class="panel empty-panel">
          <div class="panel-header original-header">
            <span>Original Photos</span>
          </div>
          <div class="panel-body">
            <span class="no-photos-text">No original photos available</span>
          </div>
        </div>

        <div class="panel-divider" />

        <div class="panel">
          <div class="panel-header reinspection-header">
            <span>Reinspection Photos</span>
            <span class="photo-counter"
              >{{ reinspectionIndex + 1 }}/{{ reinspectionPhotos.length }}</span
            >
          </div>
          <div class="panel-body">
            <button
              v-if="reinspectionPhotos.length > 1"
              class="nav-arrow left"
              aria-label="Previous reinspection photo"
              @click="prevReinspection"
            >
              <span class="fa-solid fa-chevron-left" />
            </button>
            <div class="panel-image">
              <img
                v-if="
                  currentReinspectionPhoto &&
                  !currentReinspectionPhoto.attributes.video
                "
                :src="resolveUrl(currentReinspectionPhoto)"
                alt="Reinspection photo"
              />
              <video
                v-else-if="
                  currentReinspectionPhoto &&
                  currentReinspectionPhoto.attributes.video
                "
                controls
                preload="auto"
                :poster="
                  cdnUrl(currentReinspectionPhoto.attributes.video_thumb)
                "
              >
                <source
                  :src="cdnUrl(currentReinspectionPhoto.attributes.video_url)"
                  type="video/mp4"
                />
              </video>
            </div>
            <button
              v-if="reinspectionPhotos.length > 1"
              class="nav-arrow right"
              aria-label="Next reinspection photo"
              @click="nextReinspection"
            >
              <span class="fa-solid fa-chevron-right" />
            </button>
            <div
              v-if="hasCaption(currentReinspectionPhoto)"
              class="photo-caption"
            >
              {{ currentReinspectionPhoto.attributes.caption }}
            </div>
          </div>
          <div class="panel-thumbnails">
            <div
              v-for="(photo, idx) in reinspectionPhotos"
              :key="photo.id"
              class="thumb"
              :class="{ active: idx === reinspectionIndex }"
              @click="reinspectionIndex = idx"
            >
              <img :src="resolveThumb(photo)" />
            </div>
          </div>
        </div>
      </div>
      <div class="top-actions">
        <button class="action-btn" aria-label="Close viewer" @click="close">
          <span class="fa-solid fa-xmark" />
        </button>
      </div>
    </template>

    <!-- Mobile: tabbed layout -->
    <template v-else>
      <div class="mobile-viewer">
        <div class="mobile-top-bar">
          <span class="mobile-counter"
            >{{ activeIndex + 1 }} / {{ activePhotos.length }}</span
          >
          <div class="mobile-actions">
            <button
              class="mobile-action-btn"
              aria-label="Close viewer"
              @click="close"
            >
              <span class="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        <div class="mobile-tabs">
          <button
            v-if="originalPhotos.length > 0"
            class="tab-pill"
            :class="{ active: activeTab === 'original' }"
            @click="switchTab('original')"
          >
            Original
            <span class="tab-count">{{ originalPhotos.length }}</span>
          </button>
          <button
            class="tab-pill"
            :class="{ active: activeTab === 'reinspection' }"
            @click="switchTab('reinspection')"
          >
            Reinspection
            <span class="tab-count">{{ reinspectionPhotos.length }}</span>
          </button>
        </div>

        <div class="mobile-photo-area">
          <button
            v-if="activePhotos.length > 1"
            class="nav-arrow left"
            aria-label="Previous photo"
            @click="prevMobile"
          >
            <span class="fa-solid fa-chevron-left" />
          </button>
          <div class="mobile-image">
            <img
              v-if="currentMobilePhoto && !currentMobilePhoto.attributes.video"
              :src="resolveUrl(currentMobilePhoto)"
              alt="Photo"
            />
            <video
              v-else-if="
                currentMobilePhoto && currentMobilePhoto.attributes.video
              "
              controls
              preload="auto"
              :poster="cdnUrl(currentMobilePhoto.attributes.video_thumb)"
            >
              <source
                :src="cdnUrl(currentMobilePhoto.attributes.video_url)"
                type="video/mp4"
              />
            </video>
          </div>
          <button
            v-if="activePhotos.length > 1"
            class="nav-arrow right"
            aria-label="Next photo"
            @click="nextMobile"
          >
            <span class="fa-solid fa-chevron-right" />
          </button>
          <div v-if="hasCaption(currentMobilePhoto)" class="photo-caption">
            {{ currentMobilePhoto.attributes.caption }}
          </div>
        </div>

        <div class="mobile-thumbnails">
          <div
            v-for="(photo, idx) in activePhotos"
            :key="photo.id"
            class="thumb"
            :class="{ active: idx === activeIndex }"
            @click="activeIndex = idx"
          >
            <img :src="resolveThumb(photo)" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { replaceWithCdnUrl } from '@/utils/cdnHelper'
import { resolvePhotoUrl, resolveThumbUrl } from '@/utils/photoUrlResolver'

export default {
  data() {
    return {
      reinspectionIndex: 0,
      originalIndex: 0,
      activeTab: 'original',
      activeIndex: 0,
    }
  },
  computed: {
    ...mapState('reports', ['comparisonViewer']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    reinspectionPhotos() {
      return (
        (this.comparisonViewer && this.comparisonViewer.reinspectionPhotos) ||
        []
      )
    },
    originalPhotos() {
      return (
        (this.comparisonViewer && this.comparisonViewer.originalPhotos) || []
      )
    },
    currentReinspectionPhoto() {
      return this.reinspectionPhotos[this.reinspectionIndex] || null
    },
    currentOriginalPhoto() {
      return this.originalPhotos[this.originalIndex] || null
    },
    activePhotos() {
      return this.activeTab === 'reinspection'
        ? this.reinspectionPhotos
        : this.originalPhotos
    },
    currentMobilePhoto() {
      return this.activePhotos[this.activeIndex] || null
    },
  },
  mounted() {
    if (this.comparisonViewer) {
      this.reinspectionIndex =
        this.comparisonViewer.initialReinspectionIndex || 0
      this.originalIndex = this.comparisonViewer.initialOriginalIndex || 0
    }
    document.addEventListener('keydown', this.handleKeydown)
    document.body.style.overflow = 'hidden'
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
    document.body.style.overflow = ''
  },
  methods: {
    cdnUrl(url) {
      return replaceWithCdnUrl(url) || url
    },
    resolveUrl(photo) {
      return resolvePhotoUrl(photo)
    },
    resolveThumb(photo) {
      return resolveThumbUrl(photo)
    },
    hasCaption(photo) {
      return !!photo && (photo.attributes.caption || '').length > 0
    },
    close() {
      this.$store.dispatch('reports/closeComparisonViewer')
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.close()
      }
    },
    prevReinspection() {
      this.reinspectionIndex =
        (this.reinspectionIndex - 1 + this.reinspectionPhotos.length) %
        this.reinspectionPhotos.length
    },
    nextReinspection() {
      this.reinspectionIndex =
        (this.reinspectionIndex + 1) % this.reinspectionPhotos.length
    },
    prevOriginal() {
      this.originalIndex =
        (this.originalIndex - 1 + this.originalPhotos.length) %
        this.originalPhotos.length
    },
    nextOriginal() {
      this.originalIndex = (this.originalIndex + 1) % this.originalPhotos.length
    },
    switchTab(tab) {
      this.activeTab = tab
      const photos =
        tab === 'reinspection' ? this.reinspectionPhotos : this.originalPhotos
      this.activeIndex =
        photos.length > 0 ? Math.min(this.activeIndex, photos.length - 1) : 0
    },
    prevMobile() {
      this.activeIndex =
        (this.activeIndex - 1 + this.activePhotos.length) %
        this.activePhotos.length
    },
    nextMobile() {
      this.activeIndex = (this.activeIndex + 1) % this.activePhotos.length
    },
  },
}
</script>

<style lang="scss" scoped>
.media-comparison-viewer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  background-color: #000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

// Desktop
.viewer-panels {
  display: flex;
  width: 100%;
  height: 100%;
  padding-top: 64px;
  gap: 24px;
  justify-content: center;
  align-items: flex-start;
}

.panel {
  flex: 1 1 0;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  height: calc(100% - 64px);
}

.panel-divider {
  width: 1px;
  align-self: stretch;
  background: rgba(255, 255, 255, 0.15);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  font-size: 12px;
  font-weight: 400;
  color: #fff;
  letter-spacing: 1.2px;
  line-height: 18px;
  width: 100%;
}

.reinspection-header {
  background-color: #0064a3;
}

.original-header {
  background-color: rgba(255, 255, 255, 0.12);
}

.photo-counter {
  opacity: 0.85;
}

.panel-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 0;
  width: 100%;
  padding-bottom: 52px;
}

.panel-image {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  video {
    max-width: 100%;
    max-height: 100%;
  }
}

.photo-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 8px 16px;
  font-size: 12px;
  line-height: 18px;
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
  text-align: center;
  max-height: 52px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.nav-arrow {
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(6px);
  color: #fff;
  border: none;
  font-size: 14px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background 0.15s;
  z-index: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.6);
  }

  &.left {
    position: absolute;
    left: 8px;
  }

  &.right {
    position: absolute;
    right: 8px;
  }
}

.panel-thumbnails {
  display: flex;
  gap: 8px;
  padding: 8px;
  overflow-x: auto;
  justify-content: center;
}

.thumb {
  width: 80px;
  height: 107px;
  flex-shrink: 0;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.15s;
  border: 2px solid transparent;
  background: rgba(255, 255, 255, 0.2);

  &.active {
    opacity: 1;
    border-color: #fff;
  }

  &:hover {
    opacity: 0.85;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.no-photos-text {
  color: #999;
  font-size: 14px;
}

.top-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 1px solid #d1d5db;
  background: none;
  color: #647382;
  font-size: 14px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

// Mobile
.mobile-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.mobile-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  color: #333;
}

.mobile-counter {
  font-size: 14px;
  color: #555;
}

.mobile-actions {
  display: flex;
  gap: 16px;
}

.mobile-action-btn {
  background: none;
  border: none;
  color: #333;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
}

.mobile-tabs {
  display: flex;
  justify-content: center;
  padding: 4px;
  background: #e8e8e8;
  border-radius: 24px;
  margin: 0 16px 8px;
}

.tab-pill {
  border: none;
  padding: 8px 20px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #555;
  background: transparent;
  transition: all 0.2s;

  &.active {
    background: #0064a3;
    color: #fff;
  }
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 11px;
  margin-left: 4px;
  vertical-align: middle;
}

.mobile-photo-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 0;
  background: #000;
  padding-bottom: 52px;
}

.mobile-image {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  video {
    max-width: 100%;
    max-height: 100%;
  }
}

.mobile-thumbnails {
  display: flex;
  gap: 4px;
  padding: 8px;
  overflow-x: auto;
  justify-content: center;
  background: #fff;
}
</style>
